import { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Edit2, Trash2, AlertTriangle, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';

export function FiguresTab({ figures, fetchFigures, currentUser }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    number: '',
    display_number: '',
    default_starting_foot: 'Either',
    description: ''
  });
  const [showPermissionWarning, setShowPermissionWarning] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [viewOnly, setViewOnly] = useState(false);
  
  const isSuperUser = currentUser?.role === 'super';
  
  const resetForm = () => {
    setFormData({
      number: '',
      display_number: '',
      default_starting_foot: 'Either',
      description: ''
    });
    setEditingItem(null);
    setViewOnly(false);
  };
  
  const handleAdd = () => {
    if (!isSuperUser) {
      setShowPermissionWarning(true);
      return;
    }
    resetForm();
    setDialogOpen(true);
  };
  
  const handleEdit = (figure) => {
    if (!isSuperUser) {
      setShowPermissionWarning(true);
      return;
    }
    setFormData({
      number: figure.number.toString(),
      display_number: figure.display_number || '',
      default_starting_foot: figure.default_starting_foot,
      description: figure.description
    });
    setEditingItem(figure);
    setViewOnly(false);
    setDialogOpen(true);
  };

  const handleView = (figure) => {
    setFormData({
      number: figure.number.toString(),
      display_number: figure.display_number || '',
      default_starting_foot: figure.default_starting_foot,
      description: figure.description
    });
    setEditingItem(figure);
    setViewOnly(true);
    setDialogOpen(true);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      number: parseInt(formData.number),
      display_number: formData.display_number || null
    };
    
    try {
      if (editingItem) {
        await axios.patch(`${API}/content/figures/${editingItem.figure_id}`, submitData, { withCredentials: true });
        toast.success('Figure updated!');
      } else {
        await axios.post(`${API}/content/figures`, submitData, { withCredentials: true });
        toast.success('Figure created!');
      }
      setDialogOpen(false);
      resetForm();
      fetchFigures();
    } catch (error) {
      toast.error('Failed to save figure');
    }
  };
  
  const handleDeleteClick = (id) => {
    if (!isSuperUser) {
      setShowPermissionWarning(true);
      return;
    }
    const figure = figures.find(f => f.figure_id === id);
    setItemToDelete(figure);
    setDeleteDialogOpen(true);
  };
  
  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/content/figures/${itemToDelete.figure_id}`, { withCredentials: true });
      toast.success('Figure deleted');
      fetchFigures();
    } catch (error) {
      toast.error('Failed to delete figure');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };
  
  const getDisplayNumber = (figure) => {
    return figure.display_number ? `${figure.number}${figure.display_number}` : figure.number.toString();
  };
  
  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Figures</h2>
        <Button onClick={handleAdd} className="btn-primary" data-testid="add-figure-button">
          <Plus className="mr-2 h-4 w-4" />
          Add Figure
        </Button>
      </div>
      
      {!isSuperUser && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
          Figures can only be modified by Super Users. You can view all figures and use them in your grades.
        </div>
      )}
      
      <Card className="p-6">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-stone-200">
                <th className="text-left py-3 px-2 font-semibold w-[80px]">Number</th>
                <th className="text-center py-3 px-2 font-semibold w-[200px]">Starting Foot</th>
                <th className="text-left py-3 px-2 font-semibold">Description</th>
                <th className="text-center py-3 px-2 font-semibold w-[200px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {figures.map(figure => (
                <tr key={figure.figure_id} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`figure-row-${figure.figure_id}`}>
                  <td className="py-3 px-2 font-medium w-[80px]">{getDisplayNumber(figure)}</td>
                  <td className="py-3 px-2 text-sm text-center w-[200px]">{figure.default_starting_foot}</td>
                  <td className="py-3 px-2 text-sm">{figure.description}</td>
                  <td className="py-3 px-2 w-[200px]">
                    <div className="flex items-center justify-center gap-2">
                      <button onClick={() => handleView(figure)} className="action-icon" title="View" data-testid={`view-figure-${figure.figure_id}`}>
                        <Eye className="h-4 w-4" />
                      </button>
                      {isSuperUser && (
                        <>
                          <button onClick={() => handleEdit(figure)} className="action-icon" title="Edit" data-testid={`edit-figure-${figure.figure_id}`}>
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button onClick={() => handleDeleteClick(figure.figure_id)} className="action-icon" title="Delete" data-testid={`delete-figure-${figure.figure_id}`}>
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {figures.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              No figures found.
            </div>
          )}
        </div>
      </Card>
      
      {/* Permission Warning Dialog */}
      <Dialog open={showPermissionWarning} onOpenChange={setShowPermissionWarning}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="h-5 w-5" />
              Permission Required
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-stone-700">
              Please contact a Super User to add or modify Figures.
            </p>
            <p className="text-sm text-stone-500 mt-2">
              Only Super Users can manage the Figures library to ensure consistency across all competitions.
            </p>
          </div>
          <Button onClick={() => setShowPermissionWarning(false)} className="w-full">
            Understood
          </Button>
        </DialogContent>
      </Dialog>
      
      {/* Edit Dialog (Super Users only) */}
      <Dialog open={dialogOpen} onOpenChange={(val) => { setDialogOpen(val); if (!val) resetForm(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{viewOnly ? 'View Figure' : editingItem ? 'Edit Figure' : 'Create Figure'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Number * (Max 3 digits)</Label>
                <Input 
                  type="number" 
                  value={formData.number} 
                  onChange={(e) => setFormData({...formData, number: e.target.value.slice(0, 3)})} 
                  required 
                  max={999}
                  disabled={viewOnly}
                  data-testid="figure-number-input" 
                />
              </div>
              <div>
                <Label>Display Number (a, b, c, or d)</Label>
                <select className="w-full border rounded-lg px-3 py-2" value={formData.display_number} onChange={(e) => setFormData({...formData, display_number: e.target.value})} disabled={viewOnly} data-testid="figure-display-select">
                  <option value="">None</option>
                  <option value="a">a</option>
                  <option value="b">b</option>
                  <option value="c">c</option>
                  <option value="d">d</option>
                </select>
              </div>
            </div>
            <div>
              <Label>Default Starting Foot</Label>
              <div className="flex gap-6 mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="foot" value="Right" checked={formData.default_starting_foot === 'Right'} onChange={(e) => setFormData({...formData, default_starting_foot: e.target.value})} disabled={viewOnly} data-testid="foot-right" />
                  <span>Right</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="foot" value="Left" checked={formData.default_starting_foot === 'Left'} onChange={(e) => setFormData({...formData, default_starting_foot: e.target.value})} disabled={viewOnly} data-testid="foot-left" />
                  <span>Left</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="foot" value="Either" checked={formData.default_starting_foot === 'Either'} onChange={(e) => setFormData({...formData, default_starting_foot: e.target.value})} disabled={viewOnly} data-testid="foot-either" />
                  <span>Either</span>
                </label>
              </div>
            </div>
            <div>
              <Label>Description *</Label>
              <Input value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} required disabled={viewOnly} data-testid="figure-description-input" />
            </div>
            {!viewOnly && (
              <Button type="submit" className="w-full btn-primary" data-testid="figure-submit-button">
                {editingItem ? 'Update Figure' : 'Create Figure'}
              </Button>
            )}
          </form>
        </DialogContent>
      </Dialog>
      
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleConfirmDelete}
        itemName={itemToDelete ? `Figure "${getDisplayNumber(itemToDelete)}"` : 'this figure'}
        loading={deleting}
      />
    </>
  );
}
